import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle, Crown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { paymentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshProfile } = useAuth();
  const [state, setState] = useState('polling'); // polling | paid | failed | expired
  const [message, setMessage] = useState('Verifying your payment...');

  const poll = useCallback(
    async (sessionId, attempt = 0) => {
      if (attempt >= MAX_ATTEMPTS) {
        setState('failed');
        setMessage('Payment status check timed out. If you were charged, contact support.');
        return;
      }
      try {
        const data = await paymentsApi.getStatus(sessionId);
        if (data.payment_status === 'paid') {
          setState('paid');
          setMessage('Payment successful — welcome to Pro!');
          try {
            await refreshProfile();
          } catch (err) {
            // non-fatal — profile will refresh on next navigation.
            console.debug('[payment] refreshProfile after paid failed:', err?.message);
          }
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          setMessage('Your checkout session expired. Please try again.');
          return;
        }
        setMessage('Payment is being processed...');
        setTimeout(() => poll(sessionId, attempt + 1), POLL_INTERVAL_MS);
      } catch (err) {
        setState('failed');
        setMessage(err.message || 'Could not verify payment');
      }
    },
    [refreshProfile]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState('failed');
      setMessage('Missing session_id in URL.');
      return;
    }
    poll(sessionId);
  }, [location.search, poll]);

  const icon = () => {
    if (state === 'polling') return <Loader2 className="w-14 h-14 animate-spin text-[#00BFFF]" />;
    if (state === 'paid') return <CheckCircle2 className="w-14 h-14 text-[#00FF00]" />;
    return <XCircle className="w-14 h-14 text-[#FF0055]" />;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="payment-success-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 py-24 mt-16">
        <Card className="bg-[#1A1A1A] border-gray-800">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">{icon()}</div>
            <CardTitle className="text-white text-2xl">
              {state === 'polling' && 'Verifying payment'}
              {state === 'paid' && (
                <span className="flex items-center justify-center gap-2">
                  <Crown className="w-6 h-6 text-[#00FF00]" />
                  You are Pro
                </span>
              )}
              {state === 'expired' && 'Checkout expired'}
              {state === 'failed' && 'Something went wrong'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-300" data-testid="payment-status-message">
              {message}
            </p>
            <div className="flex justify-center gap-3 pt-2">
              {state === 'paid' && (
                <Button
                  data-testid="go-to-dashboard-btn"
                  className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              )}
              {(state === 'failed' || state === 'expired') && (
                <Button
                  data-testid="try-again-btn"
                  className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
                  onClick={() => navigate('/pricing')}
                >
                  Back to Pricing
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentSuccess;
